import { addDays, differenceInCalendarDays, format, isValid, parseISO } from 'date-fns';
import {
  WITH_OTB_HIERARCHY_OPTIONS as SHARED_WITH_OTB_HIERARCHY_OPTIONS,
  WITHOUT_OTB_HIERARCHY_OPTIONS as SHARED_WITHOUT_OTB_HIERARCHY_OPTIONS,
  WEEKS_PER_YEAR,
  computeSellThroughPct,
  computeSellThroughStartInvPct,
  formatTargetWeeks,
  resolveDateRange,
} from '../topSellers/TopSellersReport.component';
import { csvCell, ON_ORDER_SPAN_OPTIONS } from '../vendorPerformance/VendorPerformanceReport.component';
import {
  EMPTY_CELL,
  compareNullsLast,
  formatCurrency,
  formatDate,
  formatPercent,
  formatUnits,
  resolveSkuColorSize,
  rollupLabel,
} from '../agedInventory/agedInventory.logic';
import type {
  ClassPlanRow,
  ClientSettingsRow,
  CoreItemsFilter,
  CoreReplenishmentApiResponse,
  CoreReplenishmentControls,
  CoreReplenishmentFilterState,
  CoreReplenishmentGroup,
  CoreReplenishmentHierarchy,
  CoreReplenishmentProduct,
  CoreReplenishmentProductRow,
  CoreReplenishmentSku,
  CoreReplenishmentSkuRow,
  InsufficientReason,
  NumericSettingKey,
  OnOrderSpanKey,
  OpenPo,
  OtbFigures,
  PinState,
  ReplenishmentSettingKey,
  ReplenishmentSettings,
  ReplenishmentStatus,
  RowFigures,
  SalesWindowFigures,
  SettingConfig,
  SettingSource,
  SkuGrain,
  StatusCounts,
  ValueBasis,
  VendorTerms,
} from './coreReplenishment.types';

export { EMPTY_CELL, formatCurrency, formatDate, formatPercent, formatUnits, resolveDateRange };

const DAYS_PER_WEEK = 7;

// --- Status --------------------------------------------------------------------------------------

export interface StatusConfig {
  key: ReplenishmentStatus;
  label: string;
  // Lower is more urgent.
  rank: number;
  badgeClass: string;
}

export const STATUS_OPTIONS: readonly StatusConfig[] = [
  {
    key: 'order_now',
    label: 'Order now',
    rank: 0,
    badgeClass: 'bg-[#FEF3F2] text-[#B42318] border border-[#FECDCA]',
  },
  {
    key: 'next_order',
    label: 'Next order',
    rank: 1,
    badgeClass: 'bg-[#FFFAEB] text-[#B54708] border border-[#FEDF89]',
  },
  {
    key: 'covered',
    label: 'Covered',
    rank: 2,
    badgeClass: 'bg-[#ECFDF3] text-[#067647] border border-[#ABEFC6]',
  },
  {
    key: 'insufficient_data',
    label: 'Insufficient data',
    rank: 3,
    badgeClass: 'bg-[#F4F4F5] text-[#52525B] border border-[#E4E4E7]',
  },
];

const STATUS_BY_KEY = new Map<ReplenishmentStatus, StatusConfig>(STATUS_OPTIONS.map((s) => [s.key, s]));

export const statusConfig = (key: ReplenishmentStatus): StatusConfig => STATUS_BY_KEY.get(key) as StatusConfig;
export const statusLabel = (key: ReplenishmentStatus | null): string => (key ? statusConfig(key).label : EMPTY_CELL);
const statusRank = (key: ReplenishmentStatus | null): number => (key ? statusConfig(key).rank : STATUS_OPTIONS.length);

/** Most urgent status; insufficient data only wins when every item has it. */
export function mostUrgentStatus(statuses: (ReplenishmentStatus | null)[]): ReplenishmentStatus | null {
  return statuses.reduce<ReplenishmentStatus | null>((best, s) => (statusRank(s) < statusRank(best) ? s : best), null);
}

export const emptyStatusCounts = (): StatusCounts => ({
  order_now: 0,
  next_order: 0,
  covered: 0,
  insufficient_data: 0,
});

export function countStatuses(items: { status: ReplenishmentStatus | null }[]): StatusCounts {
  const counts = emptyStatusCounts();
  items.forEach((item) => {
    if (item.status) counts[item.status] += 1;
  });
  return counts;
}

/** "4 order now" for the group's most urgent status. */
export function statusCountLabel(counts: StatusCounts): string | null {
  const top = mostUrgentStatus(STATUS_OPTIONS.filter((s) => counts[s.key] > 0).map((s) => s.key));
  return top ? `${counts[top]} ${statusLabel(top).toLowerCase()}` : null;
}

export const INSUFFICIENT_REASON_TEXT: Record<InsufficientReason, string> = {
  no_receiving_feed: 'No receiving feed from this source, so in-stock days cannot be rebuilt. No order is suggested.',
  low_in_stock_days: 'Fewer in-stock days than the minimum in the velocity lookback. No order is suggested.',
};

// --- Settings ------------------------------------------------------------------------------------

export const DEFAULT_SETTINGS: ReplenishmentSettings = {
  leadTimeWeeks: 2,
  orderCadenceWeeks: 2,
  coreUplift: 1.25,
  includeLeadAndCadence: true,
  velocityLookbackWeeks: 12,
  minInStockDays: 14,
  trendFlagPct: 25,
  orderNowMultiplier: 1,
  nextOrderMultiplier: 1.5,
  coreSoldWeeks: 9,
  coreWindowWeeks: 12,
  minUnitsPerWeek: 1,
};

export const SETTINGS_CONFIG: readonly SettingConfig[] = [
  {
    key: 'leadTimeWeeks',
    section: 'Order timing',
    label: 'Lead time',
    subtext: 'Default for vendors without PO history',
    description: 'Weeks from placing an order to receiving it. Undated POs are expected after this long.',
    unit: 'wks',
    min: 0.5,
    max: 26,
    step: 0.5,
    clientLevel: true,
  },
  {
    key: 'orderCadenceWeeks',
    section: 'Order timing',
    label: 'Order cadence',
    subtext: 'How often you order from a vendor',
    description: 'Weeks between regular orders.',
    unit: 'wks',
    min: 0.5,
    max: 26,
    step: 0.5,
    clientLevel: true,
  },
  {
    key: 'coreUplift',
    section: 'Cover',
    label: 'Core uplift',
    subtext: 'Extra cover for core items',
    description: 'Multiplies the plan target cover for core items.',
    unit: '×',
    min: 1,
    max: 3,
    step: 0.05,
  },
  {
    key: 'velocityLookbackWeeks',
    section: 'Velocity',
    label: 'Velocity lookback',
    subtext: 'Weeks of sales used for velocity',
    description: 'Weeks of sales the daily velocity is measured over.',
    unit: 'wks',
    min: 1,
    max: 26,
    step: 1,
  },
  {
    key: 'minInStockDays',
    section: 'Velocity',
    label: 'Minimum in-stock days',
    subtext: 'Fewer days shows Insufficient data',
    description: 'Fewer in-stock days in the lookback marks an item Insufficient data.',
    unit: 'days',
    min: 1,
    max: 84,
    step: 1,
  },
  {
    key: 'trendFlagPct',
    section: 'Velocity',
    label: 'Trend flag',
    subtext: 'Highlight changes of at least',
    description: 'Flags a velocity change at or beyond this, last 4 weeks vs the prior 4.',
    unit: '%',
    min: 5,
    max: 100,
    step: 1,
  },
  {
    key: 'orderNowMultiplier',
    section: 'Status',
    label: 'Order now',
    subtext: 'Runs out within',
    description: 'Order now when stock runs out within this multiple of lead time + cadence.',
    unit: '×',
    min: 0.25,
    max: 3,
    step: 0.05,
  },
  {
    key: 'nextOrderMultiplier',
    section: 'Status',
    label: 'Next order',
    subtext: 'Runs out within',
    description: 'Next order when stock runs out within this multiple. Must be above Order now.',
    unit: '×',
    min: 0.5,
    max: 6,
    step: 0.05,
  },
  {
    key: 'coreSoldWeeks',
    section: 'Core items',
    label: 'Sold in N weeks',
    subtext: 'Sold in this many weeks. Pin or unpin items in the table.',
    description: 'Weeks with a sale needed to detect a core item.',
    unit: 'wks',
    min: 1,
    max: 26,
    step: 1,
    clientLevel: true,
  },
  {
    key: 'coreWindowWeeks',
    section: 'Core items',
    label: 'Of the last M weeks',
    subtext: 'Weeks looked back over to detect a core item.',
    description: 'Weeks looked back over to detect a core item.',
    unit: 'wks',
    min: 4,
    max: 26,
    step: 1,
    clientLevel: true,
  },
  {
    key: 'minUnitsPerWeek',
    section: 'Core items',
    label: 'Minimum units per week',
    subtext: 'Net units a week must sell to count as a week with sales',
    description: 'Net units a week needs to count as a week with sales.',
    unit: 'units',
    min: 1,
    max: 100,
    step: 1,
    clientLevel: true,
  },
];

const SETTING_BY_KEY = new Map<NumericSettingKey, SettingConfig>(SETTINGS_CONFIG.map((c) => [c.key, c]));
const NUMERIC_SETTING_KEYS = SETTINGS_CONFIG.map((c) => c.key);
export const CLIENT_SETTING_KEYS: readonly NumericSettingKey[] = SETTINGS_CONFIG.filter((c) => c.clientLevel).map((c) => c.key);
const ALL_SETTING_KEYS: readonly ReplenishmentSettingKey[] = [...NUMERIC_SETTING_KEYS, 'includeLeadAndCadence'];

export const settingConfig = (key: NumericSettingKey): SettingConfig => SETTING_BY_KEY.get(key) as SettingConfig;

export function clampSetting(key: NumericSettingKey, value: number): number {
  const { min, max, step } = settingConfig(key);
  if (!Number.isFinite(value)) return DEFAULT_SETTINGS[key];
  const stepped = Math.round(value / step) * step;
  return Math.min(max, Math.max(min, Number(stepped.toFixed(4))));
}

/** Errors keyed by the setting to flag; empty when the settings can be applied. */
export function validateSettings(s: ReplenishmentSettings): Partial<Record<NumericSettingKey, string>> {
  const errors: Partial<Record<NumericSettingKey, string>> = {};
  SETTINGS_CONFIG.forEach(({ key, min, max }) => {
    if (!Number.isFinite(s[key]) || s[key] < min || s[key] > max) errors[key] = `Enter ${min} to ${max}.`;
  });
  if (!errors.nextOrderMultiplier && s.nextOrderMultiplier <= s.orderNowMultiplier) errors.nextOrderMultiplier = 'Must be above Order now.';
  if (!errors.coreSoldWeeks && s.coreSoldWeeks > s.coreWindowWeeks) errors.coreSoldWeeks = 'N cannot exceed M.';
  return errors;
}

/** Only known keys with usable values; for saved views and user edits. */
export function sanitizePartialSettings(raw: unknown): Partial<ReplenishmentSettings> {
  const source = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const out: Partial<ReplenishmentSettings> = {};
  NUMERIC_SETTING_KEYS.forEach((key) => {
    const value = source[key];
    if (typeof value === 'number' && Number.isFinite(value)) out[key] = clampSetting(key, value);
  });
  if (typeof source.includeLeadAndCadence === 'boolean') out.includeLeadAndCadence = source.includeLeadAndCadence;
  return out;
}

const positive = (value: number | null | undefined): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0;

/** core_replenishment_settings (days) to settings (weeks); null columns stay on the defaults. */
export function clientSettingsFromRow(row: ClientSettingsRow | null | undefined): Partial<ReplenishmentSettings> {
  if (!row) return {};
  const out: Partial<ReplenishmentSettings> = {};
  if (positive(row.lead_time_days)) out.leadTimeWeeks = clampSetting('leadTimeWeeks', row.lead_time_days / DAYS_PER_WEEK);
  if (positive(row.order_cadence_days)) out.orderCadenceWeeks = clampSetting('orderCadenceWeeks', row.order_cadence_days / DAYS_PER_WEEK);
  if (positive(row.core_weeks_n)) out.coreSoldWeeks = clampSetting('coreSoldWeeks', row.core_weeks_n);
  if (positive(row.core_weeks_m)) out.coreWindowWeeks = clampSetting('coreWindowWeeks', row.core_weeks_m);
  if (positive(row.min_units_per_week)) out.minUnitsPerWeek = clampSetting('minUnitsPerWeek', row.min_units_per_week);
  return out;
}

export function clientSettingsToRow(
  s: Pick<ReplenishmentSettings, 'leadTimeWeeks' | 'orderCadenceWeeks' | 'coreSoldWeeks' | 'coreWindowWeeks' | 'minUnitsPerWeek'>
): ClientSettingsRow {
  return {
    lead_time_days: Math.round(s.leadTimeWeeks * DAYS_PER_WEEK),
    order_cadence_days: Math.round(s.orderCadenceWeeks * DAYS_PER_WEEK),
    core_weeks_n: s.coreSoldWeeks,
    core_weeks_m: s.coreWindowWeeks,
    min_units_per_week: s.minUnitsPerWeek,
  };
}

export interface ResolvedSettings {
  settings: ReplenishmentSettings;
  // The settings that would apply; differs from `settings` only while they're invalid.
  requested: ReplenishmentSettings;
  errors: Partial<Record<NumericSettingKey, string>>;
  sources: Record<ReplenishmentSettingKey, SettingSource>;
}

/** Default < client < user. An invalid combination leaves the table on the client settings. */
export function resolveSettings(clientOverrides: Partial<ReplenishmentSettings>, userOverrides: Partial<ReplenishmentSettings>): ResolvedSettings {
  const client = { ...DEFAULT_SETTINGS, ...clientOverrides };
  const user = sanitizePartialSettings(userOverrides);
  const requested = { ...client, ...user };
  const errors = validateSettings(requested);
  const valid = Object.keys(errors).length === 0;
  const sources = Object.fromEntries(
    ALL_SETTING_KEYS.map((key) => {
      const source: SettingSource = key in user && user[key] !== client[key] ? 'user' : key in clientOverrides ? 'client' : 'default';
      return [key, source];
    })
  ) as Record<ReplenishmentSettingKey, SettingSource>;
  return { settings: valid ? requested : client, requested, errors, sources };
}

export const SETTING_SOURCE_LABELS: Record<SettingSource, string> = {
  default: 'Default',
  client: 'Client',
  plan: 'From plan',
  user: 'You',
};

// "4" from 4, "2.5" from 2.5: setting values as the panel and its notes print them.
export const formatSettingNumber = (value: number): string => String(Number(value.toFixed(2)));

// --- Weekly series -------------------------------------------------------------------------------

export const HISTORY_WEEKS = 26;
const TREND_WEEKS = 4;
const TREND_MIN_IN_STOCK_DAYS = 7;

const sum = (values: number[]): number => values.reduce((acc, v) => acc + (Number(v) || 0), 0);
const lastWeeks = (series: number[], weeks: number): number[] => (series ?? []).slice(Math.max(0, (series ?? []).length - weeks));

export interface VelocityResult {
  velocityPerDay: number | null;
  inStockDays: number;
  windowDays: number;
}

/** Net units ÷ in-stock days over the lookback. */
export function computeVelocity(weeklyUnits: number[], weeklyInStockDays: number[], lookbackWeeks: number): VelocityResult {
  const units = Math.max(0, sum(lastWeeks(weeklyUnits, lookbackWeeks)));
  const inStockDays = sum(lastWeeks(weeklyInStockDays, lookbackWeeks));
  const windowDays = Math.min(lookbackWeeks, (weeklyInStockDays ?? []).length) * DAYS_PER_WEEK;
  return {
    velocityPerDay: inStockDays > 0 ? units / inStockDays : null,
    inStockDays,
    windowDays,
  };
}

/** Velocity per in-stock day, last 28 days vs the prior 28; blank when either has under 7 in-stock days. */
export function computeTrendPct(weeklyUnits: number[], weeklyInStockDays: number[]): number | null {
  const units = weeklyUnits ?? [];
  const days = weeklyInStockDays ?? [];
  if (units.length < TREND_WEEKS * 2 || days.length < TREND_WEEKS * 2) return null;
  const recentDays = sum(days.slice(-TREND_WEEKS));
  const priorDays = sum(days.slice(-TREND_WEEKS * 2, -TREND_WEEKS));
  if (recentDays < TREND_MIN_IN_STOCK_DAYS || priorDays < TREND_MIN_IN_STOCK_DAYS) return null;
  const recent = sum(units.slice(-TREND_WEEKS)) / recentDays;
  const prior = sum(units.slice(-TREND_WEEKS * 2, -TREND_WEEKS)) / priorDays;
  return prior > 0 ? ((recent - prior) / prior) * 100 : null;
}

export const isTrendFlagged = (trendPct: number | null, flagPct: number): boolean => trendPct !== null && Math.abs(trendPct) >= flagPct;

/** Core when at least N of the last M weekly buckets sold the minimum units. */
export function isDetectedCore(weeklyUnits: number[], soldWeeks: number, windowWeeks: number, minUnitsPerWeek = 1): boolean {
  const threshold = Math.max(minUnitsPerWeek, Number.EPSILON);
  return lastWeeks(weeklyUnits, windowWeeks).filter((units) => units >= threshold).length >= soldWeeks;
}

export const isCoreItem = (detected: boolean, pinState: PinState | null): boolean => pinState === 'pinned' || (detected && pinState !== 'unpinned');

/** The pin icon's next state: a core item is suppressed (or unpinned), any other item is pinned. */
export function nextPinState(detected: boolean, pinState: PinState | null): PinState | null {
  if (isCoreItem(detected, pinState)) return detected ? 'unpinned' : null;
  return detected ? null : 'pinned';
}

const sumSeries = (series: number[][]): number[] => {
  const length = Math.max(0, ...series.map((s) => s.length));
  return Array.from({ length }, (_, i) => sum(series.map((s) => s[s.length - length + i] ?? 0)));
};

// In stock anywhere in the product counts as in stock for the product.
const maxSeries = (series: number[][]): number[] => {
  const length = Math.max(0, ...series.map((s) => s.length));
  return Array.from({ length }, (_, i) => Math.max(0, ...series.map((s) => s[s.length - length + i] ?? 0)));
};

// --- Dates ---------------------------------------------------------------------------------------

const toIso = (d: Date): string => format(d, 'yyyy-MM-dd');

export function addDaysIso(iso: string, days: number): string | null {
  const parsed = parseISO(iso);
  return isValid(parsed) && Number.isFinite(days) ? toIso(addDays(parsed, Math.floor(days))) : null;
}

const daysBetween = (fromIso: string, toIsoDate: string): number | null => {
  const a = parseISO(fromIso);
  const b = parseISO(toIsoDate);
  return isValid(a) && isValid(b) ? differenceInCalendarDays(b, a) : null;
};

/** Exact weeks in an inclusive date range (days ÷ 7), as GMROI/CMROI annualize elsewhere. */
export function periodWeeksOf(from: string, to: string): number {
  const days = daysBetween(from, to);
  return days === null || days < 0 ? 0 : (days + 1) / DAYS_PER_WEEK;
}

const minIso = (values: (string | null)[]): string | null => values.reduce<string | null>((m, v) => (v && (!m || v < m) ? v : m), null);
const maxIso = (values: (string | null)[]): string | null => values.reduce<string | null>((m, v) => (v && (!m || v > m) ? v : m), null);

// --- Replenishment math --------------------------------------------------------------------------

export interface PoReceipt {
  // Days from the as-of date; overdue POs land on day 0.
  day: number;
  units: number;
}

/** Open POs as receipt days, earliest first. A PO with no date lands at today + lead time. */
export function poReceipts(openPos: OpenPo[], asOfDate: string, leadTimeWeeks: number): PoReceipt[] {
  const leadDays = Math.round(leadTimeWeeks * DAYS_PER_WEEK);
  return (openPos ?? [])
    .filter((po) => po.units > 0)
    .map((po) => ({
      day: Math.max(0, po.expected_date ? (daysBetween(asOfDate, po.expected_date) ?? leadDays) : leadDays),
      units: po.units,
    }))
    .sort((a, b) => a.day - b.day);
}

export const STOCKOUT_HORIZON_DAYS = 730;

/**
 * First day stock(t) = OH + PO units landing on or before t − velocity × t reaches 0, equivalent to
 * a day-by-day walk. Null when nothing sells or stock outlasts the horizon.
 */
export function simulateStockoutDay(unitsOh: number, receipts: PoReceipt[], velocityPerDay: number | null, horizonDays = STOCKOUT_HORIZON_DAYS): number | null {
  if (velocityPerDay === null || !(velocityPerDay > 0)) return null;
  let available = Math.max(0, unitsOh || 0);
  let fromDay = 0;
  let i = 0;
  for (;;) {
    while (i < receipts.length && receipts[i].day <= fromDay) available += receipts[i++].units;
    // Tolerance keeps float noise from pushing an exact hit to the next day.
    const hitDay = Math.max(fromDay, Math.ceil(available / velocityPerDay - 1e-9));
    const nextReceipt = i < receipts.length ? receipts[i].day : Infinity;
    if (hitDay < nextReceipt) return hitDay > horizonDays ? null : hitDay;
    fromDay = nextReceipt;
  }
}

/** PO units that land by the stockout day; all of them when stock never runs out. */
export const unitsArrivingBy = (receipts: PoReceipt[], stockoutDay: number | null): number =>
  sum(receipts.filter((r) => stockoutDay === null || r.day <= stockoutDay).map((r) => r.units));

export interface StatusInput {
  sufficient: boolean;
  daysToStockout: number | null;
  terms: VendorTerms;
  settings: Pick<ReplenishmentSettings, 'orderNowMultiplier' | 'nextOrderMultiplier'>;
}

export function classifyStatus({ sufficient, daysToStockout, terms, settings }: StatusInput): ReplenishmentStatus {
  if (!sufficient) return 'insufficient_data';
  if (daysToStockout === null) return 'covered';
  const restockDays = (terms.lead_time_weeks + terms.cadence_weeks) * DAYS_PER_WEEK;
  if (daysToStockout <= settings.orderNowMultiplier * restockDays) return 'order_now';
  if (daysToStockout <= settings.nextOrderMultiplier * restockDays) return 'next_order';
  return 'covered';
}

/** Weeks of supply T: the override, else plan cover × uplift (lead + cadence without a plan). */
export function resolveTargetWeeks(
  planCoverWeeks: number | null,
  terms: VendorTerms,
  settings: Pick<ReplenishmentSettings, 'coreUplift'>,
  wosOverride: number | null
): number {
  if (wosOverride !== null) return wosOverride;
  const base = positive(planCoverWeeks) ? planCoverWeeks : terms.lead_time_weeks + terms.cadence_weeks;
  return base * settings.coreUplift;
}

export const MIN_WOS_OVERRIDE = 1;
export const MAX_WOS_OVERRIDE = 52;

/** A whole number 1–52, or null (clears back to the plan) for anything else. */
export function parseWosOverride(input: string | number | null | undefined): number | null {
  if (input === '' || input === null || input === undefined) return null;
  const value = typeof input === 'number' ? input : Number(String(input).trim());
  return Number.isInteger(value) && value >= MIN_WOS_OVERRIDE && value <= MAX_WOS_OVERRIDE ? value : null;
}

export const targetFieldLabel = (wosOverride: number | null, settings: Pick<ReplenishmentSettings, 'coreUplift'>): string =>
  wosOverride !== null ? `${wosOverride} wks` : `plan × ${settings.coreUplift}`;

/**
 * The plan target the Weeks of supply field shows while it has no override: "5.8", or "4.5–7.5"
 * when the products' classes plan different cover. Null with no products.
 */
export function planTargetText(
  products: Pick<CoreReplenishmentProduct, 'row' | 'terms'>[],
  ctx: Pick<ComputeContext, 'classPlans' | 'settings'>
): string | null {
  return weeksRangeText(
    products.map((p) => {
      const plan = p.row.class_code ? ctx.classPlans.get(p.row.class_code) : undefined;
      return resolveTargetWeeks(plan?.target_cover_weeks ?? null, p.terms, ctx.settings, null);
    })
  );
}

/**
 * The settings panel's Target cover: the class plans' own cover (next month's planned inventory ÷
 * its planned weekly sales) over the products shown. Classes without a plan cover are left out, so
 * null means none of them has one and the lead time + cadence default applies.
 */
export function planCoverText(products: Pick<CoreReplenishmentProduct, 'row'>[], classPlans: ReadonlyMap<string, ClassPlanRow>): string | null {
  return weeksRangeText(
    products.flatMap((p) => {
      const cover = p.row.class_code ? classPlans.get(p.row.class_code)?.target_cover_weeks : null;
      return positive(cover) ? [cover] : [];
    })
  );
}

// "5.8", or "4.5–7.5" across differing values, to one decimal; null for none.
function weeksRangeText(values: number[]): string | null {
  let min: number | null = null;
  let max: number | null = null;
  values.forEach((value) => {
    const weeks = Math.round(value * 10) / 10;
    min = min === null || weeks < min ? weeks : min;
    max = max === null || weeks > max ? weeks : max;
  });
  if (min === null || max === null) return null;
  return min === max ? formatTargetWeeks(min) : `${formatTargetWeeks(min)}–${formatTargetWeeks(max)}`;
}

export interface ReorderInput {
  velocityPerDay: number | null;
  unitsOh: number;
  unitsArrivingInTime: number;
  terms: VendorTerms;
  targetWeeks: number;
  includeLeadAndCadence: boolean;
}

/** max(0, ceil(7 × V × (L + C + T) − OH − on-order arriving in time)); L and C drop when the option is off. */
export function computeReorderUnits({ velocityPerDay, unitsOh, unitsArrivingInTime, terms, targetWeeks, includeLeadAndCadence }: ReorderInput): number {
  const weeks = (includeLeadAndCadence ? terms.lead_time_weeks + terms.cadence_weeks : 0) + targetWeeks;
  const demand = DAYS_PER_WEEK * Math.max(0, velocityPerDay ?? 0) * weeks;
  return Math.max(0, Math.ceil(demand - Math.max(0, unitsOh) - Math.max(0, unitsArrivingInTime) - 1e-9));
}

export const unitPrice = (sku: Pick<CoreReplenishmentSkuRow, 'unit_cost' | 'unit_retail'>, basis: ValueBasis): number | null =>
  basis === 'cost' ? sku.unit_cost : sku.unit_retail;

const times = (units: number | null, price: number | null): number | null => (units === null || price === null ? null : units * price);

const sumNullable = (values: (number | null)[]): number | null =>
  values.some((v) => v !== null) ? values.reduce<number>((acc, v) => acc + (v ?? 0), 0) : null;

// --- Sales window --------------------------------------------------------------------------------

const EMPTY_SALES: SalesWindowFigures = {
  units_sold: 0,
  net_sales: 0,
  cash_margin: 0,
  gross_margin: 0,
  markdown_dollars: 0,
  inflow_units: 0,
  inflow_retail: 0,
  inflow_cost: 0,
  avg_inventory_cost: 0,
};

const SALES_KEYS = Object.keys(EMPTY_SALES) as (keyof SalesWindowFigures)[];

export function sumSales(list: SalesWindowFigures[]): SalesWindowFigures {
  const out = { ...EMPTY_SALES };
  list.forEach((s) =>
    SALES_KEYS.forEach((key) => {
      out[key] += Number(s?.[key]) || 0;
    })
  );
  return out;
}

type SalesRatios = Pick<
  RowFigures,
  | 'net_sales'
  | 'cash_margin'
  | 'cash_margin_pct'
  | 'cmroi'
  | 'initial_margin_pct'
  | 'gross_margin_pct'
  | 'markdown_dollars'
  | 'sell_through_pct'
  | 'sell_through_start_inv_pct'
>;

export function salesRatios(s: SalesWindowFigures, unitsOh: number, periodWeeks: number): SalesRatios {
  const pctOfSales = (value: number) => (s.net_sales > 0 ? (value / s.net_sales) * 100 : null);
  return {
    net_sales: s.net_sales,
    cash_margin: s.cash_margin,
    cash_margin_pct: pctOfSales(s.cash_margin),
    gross_margin_pct: pctOfSales(s.gross_margin),
    markdown_dollars: s.markdown_dollars,
    initial_margin_pct: s.inflow_retail > 0 ? ((s.inflow_retail - s.inflow_cost) / s.inflow_retail) * 100 : null,
    cmroi: s.avg_inventory_cost > 0 && periodWeeks > 0 ? (s.cash_margin / s.avg_inventory_cost) * (WEEKS_PER_YEAR / periodWeeks) : null,
    sell_through_pct: computeSellThroughPct(s.units_sold, s.inflow_units),
    sell_through_start_inv_pct: computeSellThroughStartInvPct(s.units_sold, Math.max(0, unitsOh)),
  };
}

// --- Compute context -----------------------------------------------------------------------------

export interface ComputeContext {
  settings: ReplenishmentSettings;
  wosOverride: number | null;
  valueBasis: ValueBasis;
  asOfDate: string;
  periodWeeks: number;
  hasReceivingFeed: boolean;
  classPlans: Map<string, ClassPlanRow>;
}

export function buildComputeContext(
  response: Pick<CoreReplenishmentApiResponse, 'class_plans' | 'as_of_date' | 'has_receiving_feed'>,
  options: {
    settings: ReplenishmentSettings;
    wosOverride: number | null;
    valueBasis: ValueBasis;
    from: string;
    to: string;
  }
): ComputeContext {
  return {
    settings: options.settings,
    wosOverride: options.wosOverride,
    valueBasis: options.valueBasis,
    asOfDate: response.as_of_date,
    periodWeeks: periodWeeksOf(options.from, options.to),
    hasReceivingFeed: response.has_receiving_feed !== false,
    classPlans: new Map((response.class_plans ?? []).map((plan) => [plan.class_code, plan])),
  };
}

// v1 has no PO-history lead times: every vendor uses the client's settings.
export const resolveVendorTerms = (settings: Pick<ReplenishmentSettings, 'leadTimeWeeks' | 'orderCadenceWeeks'>): VendorTerms => ({
  lead_time_weeks: settings.leadTimeWeeks,
  cadence_weeks: settings.orderCadenceWeeks,
});

const NO_OTB: Pick<RowFigures, 'otb' | 'otb_after' | 'otb_on_order' | 'plan_sales' | 'planned_inflow' | 'plan_next_month_inv'> = {
  otb: null,
  otb_after: null,
  otb_on_order: null,
  plan_sales: null,
  planned_inflow: null,
  plan_next_month_inv: null,
};

// --- SKU and product rows ------------------------------------------------------------------------

export function computeSku(
  row: CoreReplenishmentSkuRow,
  productId: string,
  terms: VendorTerms,
  targetWeeks: number,
  ctx: ComputeContext
): CoreReplenishmentSku {
  const { settings, valueBasis, asOfDate } = ctx;
  const { velocityPerDay, inStockDays, windowDays } = computeVelocity(row.weekly_units, row.weekly_in_stock_days, settings.velocityLookbackWeeks);
  const insufficientReason: InsufficientReason | null = !ctx.hasReceivingFeed
    ? 'no_receiving_feed'
    : inStockDays < settings.minInStockDays
      ? 'low_in_stock_days'
      : null;
  const receipts = poReceipts(row.open_pos, asOfDate, terms.lead_time_weeks);
  const onOrder = sum(receipts.map((r) => r.units));
  const daysToStockout = insufficientReason ? null : simulateStockoutDay(row.units_oh, receipts, velocityPerDay);
  const status = classifyStatus({
    sufficient: !insufficientReason,
    daysToStockout,
    terms,
    settings,
  });
  const suggestedUnits = insufficientReason
    ? null
    : computeReorderUnits({
        velocityPerDay,
        unitsOh: row.units_oh,
        unitsArrivingInTime: unitsArrivingBy(receipts, daysToStockout),
        terms,
        targetWeeks,
        includeLeadAndCadence: settings.includeLeadAndCadence,
      });
  const avgWeeklyUnits = velocityPerDay === null ? null : velocityPerDay * DAYS_PER_WEEK;
  const trendPct = computeTrendPct(row.weekly_units, row.weekly_in_stock_days);
  const { color, size } = resolveSkuColorSize(row);
  const lostSalesUnits = velocityPerDay === null ? null : velocityPerDay * Math.max(0, windowDays - inStockDays);

  return {
    id: `${productId}::${row.sku_id}`,
    row,
    size_label: size ?? EMPTY_CELL,
    color_label: color ?? EMPTY_CELL,
    expected_arrival: suggestedUnits ? addDaysIso(asOfDate, terms.lead_time_weeks * DAYS_PER_WEEK) : null,
    status,
    insufficient_reason: insufficientReason,
    units_oh: row.units_oh,
    on_order_units: onOrder,
    total_available: Math.max(0, row.units_oh) + onOrder,
    next_arrival_date: receipts.length ? addDaysIso(asOfDate, receipts[0].day) : null,
    last_order_date: row.last_order_date,
    velocity_per_day: velocityPerDay,
    in_stock_days: inStockDays,
    avg_weekly_units: avgWeeklyUnits,
    trend_pct: trendPct,
    trend_flagged: isTrendFlagged(trendPct, settings.trendFlagPct),
    days_to_stockout: daysToStockout,
    stockout_date: daysToStockout === null ? null : addDaysIso(asOfDate, daysToStockout),
    weeks_of_supply: avgWeeklyUnits ? Math.max(0, row.units_oh) / avgWeeklyUnits : null,
    target_weeks: targetWeeks,
    suggested_units: suggestedUnits,
    suggested_dollars: times(suggestedUnits, unitPrice(row, valueBasis)),
    order_cost: times(suggestedUnits, row.unit_cost),
    lost_sales: times(lostSalesUnits, unitPrice(row, valueBasis)),
    ...salesRatios(row.sales ?? EMPTY_SALES, row.units_oh, ctx.periodWeeks),
    ...NO_OTB,
  };
}

export const productRowId = (row: Pick<CoreReplenishmentProductRow, 'product_id' | 'location_code'>): string =>
  row.location_code ? `${row.product_id}@${row.location_code}` : row.product_id;

/** A product rolls up its SKUs: suggestions sum per size, so one overstocked size can't hide a stockout. */
export function computeProduct(row: CoreReplenishmentProductRow, ctx: ComputeContext): CoreReplenishmentProduct {
  const { skus: skuRows = [], ...productRow } = row;
  const id = productRowId(row);
  const { settings } = ctx;
  const terms = resolveVendorTerms(settings);
  const plan = row.class_code ? ctx.classPlans.get(row.class_code) : undefined;
  const targetWeeks = resolveTargetWeeks(plan?.target_cover_weeks ?? null, terms, settings, ctx.wosOverride);
  const skus = skuRows.map((sku) => computeSku(sku, id, terms, targetWeeks, ctx));
  const weeklyUnits = sumSeries(skuRows.map((sku) => sku.weekly_units ?? []));
  const weeklyInStock = maxSeries(skuRows.map((sku) => sku.weekly_in_stock_days ?? []));
  const unitsOh = sum(skus.map((s) => s.units_oh));
  const velocity = sumNullable(skus.map((s) => s.velocity_per_day));
  const avgWeekly = velocity === null ? null : velocity * DAYS_PER_WEEK;
  const trendPct = computeTrendPct(weeklyUnits, weeklyInStock);
  const daysToStockout = skus.reduce<number | null>(
    (m, s) => (s.days_to_stockout !== null && (m === null || s.days_to_stockout < m) ? s.days_to_stockout : m),
    null
  );
  const status = mostUrgentStatus(skus.map((s) => s.status));
  const detected = isDetectedCore(weeklyUnits, settings.coreSoldWeeks, settings.coreWindowWeeks, settings.minUnitsPerWeek);
  const inStock = skus.map((s) => s.in_stock_days ?? 0);

  return {
    id,
    row: productRow,
    skus,
    terms,
    detected_core: detected,
    is_core: isCoreItem(detected, row.pin_state),
    skus_to_order: skus.filter((s) => (s.suggested_units ?? 0) > 0).length,
    size_label: rollupLabel(
      skus.map((s) => (s.size_label === EMPTY_CELL ? null : s.size_label)),
      'sizes'
    ),
    color_label: rollupLabel(
      skus.map((s) => (s.color_label === EMPTY_CELL ? null : s.color_label)),
      'colors'
    ),
    status,
    insufficient_reason: status === 'insufficient_data' ? (skus[0]?.insufficient_reason ?? null) : null,
    units_oh: unitsOh,
    on_order_units: sum(skus.map((s) => s.on_order_units)),
    total_available: sum(skus.map((s) => s.total_available)),
    next_arrival_date: minIso(skus.map((s) => s.next_arrival_date)),
    last_order_date: maxIso(skus.map((s) => s.last_order_date)),
    velocity_per_day: velocity,
    in_stock_days: inStock.length ? Math.max(...inStock) : null,
    avg_weekly_units: avgWeekly,
    trend_pct: trendPct,
    trend_flagged: isTrendFlagged(trendPct, settings.trendFlagPct),
    days_to_stockout: daysToStockout,
    stockout_date: minIso(skus.map((s) => s.stockout_date)),
    weeks_of_supply: avgWeekly ? Math.max(0, unitsOh) / avgWeekly : null,
    target_weeks: targetWeeks,
    suggested_units: sumNullable(skus.map((s) => s.suggested_units)),
    suggested_dollars: sumNullable(skus.map((s) => s.suggested_dollars)),
    order_cost: sumNullable(skus.map((s) => s.order_cost)),
    lost_sales: sumNullable(skus.map((s) => s.lost_sales)),
    ...salesRatios(sumSales(skuRows.map((s) => s.sales ?? EMPTY_SALES)), unitsOh, ctx.periodWeeks),
    ...NO_OTB,
  };
}

export const computeProducts = (rows: CoreReplenishmentProductRow[], ctx: ComputeContext): CoreReplenishmentProduct[] =>
  (rows ?? []).map((row) => computeProduct(row, ctx));

// --- Filters and search --------------------------------------------------------------------------

export const CORE_ITEMS_OPTIONS: readonly {
  key: CoreItemsFilter;
  label: string;
}[] = [
  { key: 'detected_pinned', label: 'Detected + pinned' },
  { key: 'detected', label: 'Detected' },
  { key: 'pinned', label: 'Pinned' },
  { key: 'all', label: 'All items' },
];

export function matchesCoreFilter(
  p: Pick<CoreReplenishmentProduct, 'detected_core' | 'is_core'> & {
    row: { pin_state: PinState | null };
  },
  filter: CoreItemsFilter
): boolean {
  if (filter === 'detected') return p.detected_core && p.row.pin_state !== 'unpinned';
  if (filter === 'pinned') return p.row.pin_state === 'pinned';
  if (filter === 'detected_pinned') return p.is_core;
  return true;
}

export const SEARCH_PLACEHOLDER = 'Search product, SKU, vendor';

export function matchesSearch(p: CoreReplenishmentProduct, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const fields = [p.row.product_title, p.row.vendor, ...p.skus.flatMap((s) => [s.row.sku_id, s.row.variant_title, s.row.barcode])];
  return fields.some((value) => (value ?? '').toLowerCase().includes(q));
}

export interface ClientFilterOptions {
  statuses: ReplenishmentStatus[];
  coreItems: CoreItemsFilter;
  hideCovered: boolean;
  search: string;
}

/** The filters applied in the browser; location, category, class and vendor go to the API. */
export const filterProducts = (
  products: CoreReplenishmentProduct[],
  { statuses, coreItems, hideCovered, search }: ClientFilterOptions
): CoreReplenishmentProduct[] =>
  products.filter(
    (p) =>
      matchesCoreFilter(p, coreItems) &&
      (statuses.length === 0 || (p.status !== null && statuses.includes(p.status))) &&
      !(hideCovered && p.status === 'covered') &&
      matchesSearch(p, search)
  );

// SKU level only; product totals do not change.
export const visibleSkus = (p: CoreReplenishmentProduct, hideZeroSalesSkus: boolean): CoreReplenishmentSku[] =>
  hideZeroSalesSkus ? p.skus.filter((s) => (s.row.sales?.units_sold ?? 0) > 0) : p.skus;

// --- Columns -------------------------------------------------------------------------------------

export const PRODUCT_COLUMN_KEY = 'product_title';
export const PRODUCT_COLUMN_LABEL = 'Product';
export const PRODUCT_COLUMN_DESCRIPTION = 'Product, with its SKU and to-order counts and whether it is core. The pin marks or unmarks it as core.';
export const PRODUCT_COLUMN_WIDTH = 300;

export type CoreReplenishmentColumnKey =
  | 'size'
  | 'color'
  | 'status'
  | 'otb'
  | 'suggested_dollars'
  | 'otb_after'
  | 'stockout_date'
  | 'units_oh'
  | 'on_order_units'
  | 'velocity_per_day'
  | 'trend_pct'
  | 'weeks_of_supply'
  | 'suggested_units'
  | 'otb_on_order'
  | 'plan_sales'
  | 'planned_inflow'
  | 'plan_next_month_inv'
  | 'target_weeks'
  | 'avg_weekly_units'
  | 'total_available'
  | 'last_order_date'
  | 'order_cost'
  | 'lost_sales'
  | 'net_sales'
  | 'cash_margin'
  | 'cash_margin_pct'
  | 'cmroi'
  | 'initial_margin_pct'
  | 'gross_margin_pct'
  | 'markdown_dollars'
  | 'sell_through_pct'
  | 'sell_through_start_inv_pct';

export type CoreReplenishmentSortKey = typeof PRODUCT_COLUMN_KEY | CoreReplenishmentColumnKey;
export type ColumnGroupKey = 'default' | 'otb' | 'replenishment' | 'margin';
export type ColumnFormat = 'text' | 'status' | 'units' | 'currency' | 'date' | 'decimal' | 'weeks' | 'percent' | 'signed_percent' | 'ratio';

export interface CoreReplenishmentColumnConfig {
  key: CoreReplenishmentColumnKey;
  label: string;
  group: ColumnGroupKey;
  format: ColumnFormat;
  description: string;
  align: 'left' | 'right' | 'center';
  width: number;
  defaultVisible: boolean;
  // Dollar column that follows the Retail | Cost toggle.
  valueBasis?: boolean;
  // Hidden on Vendor by Product and Product by Location.
  otbOnly?: boolean;
  // Filled on Total, Category and Class rows; blank on products and SKUs.
  groupOnly?: boolean;
  skuOnly?: boolean;
  // Fixed second header line.
  headerSubLabel?: string;
}

export const COLUMN_GROUPS: readonly { key: ColumnGroupKey; label: string }[] = [
  { key: 'default', label: 'Shown by default' },
  { key: 'otb', label: 'OTB' },
  { key: 'replenishment', label: 'Replenishment' },
  { key: 'margin', label: 'Margin and sales' },
];

// After the sticky Product column, in design order.
export const CORE_REPLENISHMENT_COLUMNS: readonly CoreReplenishmentColumnConfig[] = [
  {
    key: 'size',
    label: 'Size',
    group: 'default',
    format: 'text',
    description: 'SKU size.',
    align: 'left',
    width: 100,
    defaultVisible: true,
    skuOnly: true,
  },
  {
    key: 'color',
    label: 'Color',
    group: 'default',
    format: 'text',
    description: 'SKU color.',
    align: 'left',
    width: 110,
    defaultVisible: true,
    skuOnly: true,
  },
  {
    key: 'status',
    label: 'Status',
    group: 'default',
    format: 'status',
    description: 'When the item runs out compared with lead time + order cadence.',
    align: 'left',
    width: 140,
    defaultVisible: true,
  },
  {
    key: 'otb',
    label: 'OTB',
    group: 'default',
    format: 'currency',
    description: 'Open-to-buy for the span, from the class plan.',
    align: 'right',
    width: 120,
    defaultVisible: true,
    valueBasis: true,
    otbOnly: true,
    groupOnly: true,
  },
  {
    key: 'suggested_dollars',
    label: 'Suggested Orders $',
    group: 'default',
    format: 'currency',
    description: 'Suggested reorder units × unit price.',
    align: 'right',
    width: 150,
    defaultVisible: true,
    valueBasis: true,
    otbOnly: true,
  },
  {
    key: 'otb_after',
    label: 'OTB After',
    group: 'default',
    format: 'currency',
    description: 'OTB less Suggested Orders $.',
    align: 'right',
    width: 120,
    defaultVisible: true,
    valueBasis: true,
    otbOnly: true,
    groupOnly: true,
  },
  {
    key: 'stockout_date',
    label: 'Stockout Date',
    group: 'default',
    format: 'date',
    description: 'First day stock reaches 0 at the current velocity, counting POs that land before then.',
    align: 'left',
    width: 130,
    defaultVisible: true,
  },
  {
    key: 'units_oh',
    label: 'Units OH',
    group: 'default',
    format: 'units',
    description: 'Units on hand now.',
    align: 'right',
    width: 100,
    defaultVisible: true,
  },
  {
    key: 'on_order_units',
    label: 'On Order',
    group: 'default',
    format: 'units',
    description: 'Open PO units, with the next arrival date.',
    align: 'right',
    width: 120,
    defaultVisible: true,
  },
  {
    key: 'velocity_per_day',
    label: 'Velocity / Day',
    group: 'default',
    format: 'decimal',
    description: 'Net units sold per in-stock day over the velocity lookback.',
    align: 'right',
    width: 130,
    defaultVisible: true,
    headerSubLabel: 'in-stock days',
  },
  {
    key: 'trend_pct',
    label: 'Trend',
    group: 'default',
    format: 'signed_percent',
    description: 'Velocity in the last 4 weeks vs the prior 4. Blank when either has under 7 in-stock days.',
    align: 'right',
    width: 120,
    defaultVisible: true,
    headerSubLabel: '4 vs prior 4 wks',
  },
  {
    key: 'weeks_of_supply',
    label: 'Weeks of Supply',
    group: 'default',
    format: 'weeks',
    description: 'Units on hand ÷ average weekly units.',
    align: 'right',
    width: 130,
    defaultVisible: true,
  },
  {
    key: 'suggested_units',
    label: 'Suggested Reorder',
    group: 'default',
    format: 'units',
    description: '7 × velocity × (lead + cadence + target weeks), less on hand and POs that land in time.',
    align: 'right',
    width: 150,
    defaultVisible: true,
  },
  {
    key: 'otb_on_order',
    label: 'On Order $',
    group: 'otb',
    format: 'currency',
    description: 'On-order dollars in the span, from the plan.',
    align: 'right',
    width: 120,
    defaultVisible: false,
    valueBasis: true,
    otbOnly: true,
    groupOnly: true,
  },
  {
    key: 'plan_sales',
    label: 'Plan Sales',
    group: 'otb',
    format: 'currency',
    description: 'Planned sales for the span.',
    align: 'right',
    width: 120,
    defaultVisible: false,
    valueBasis: true,
    otbOnly: true,
    groupOnly: true,
  },
  {
    key: 'planned_inflow',
    label: 'Planned Inflow',
    group: 'otb',
    format: 'currency',
    description: 'Planned receipts for the span.',
    align: 'right',
    width: 130,
    defaultVisible: false,
    valueBasis: true,
    otbOnly: true,
    groupOnly: true,
  },
  {
    key: 'plan_next_month_inv',
    label: 'Plan Next Mo. Inv',
    group: 'otb',
    format: 'currency',
    description: 'Planned inventory at the start of next month.',
    align: 'right',
    width: 150,
    defaultVisible: false,
    valueBasis: true,
    otbOnly: true,
    groupOnly: true,
  },
  {
    key: 'target_weeks',
    label: 'Target WOS',
    group: 'replenishment',
    format: 'weeks',
    description: 'Target weeks of supply: plan cover × uplift, or your override.',
    align: 'right',
    width: 110,
    defaultVisible: false,
  },
  {
    key: 'avg_weekly_units',
    label: 'Avg Weekly Units',
    group: 'replenishment',
    format: 'decimal',
    description: 'Velocity / Day × 7.',
    align: 'right',
    width: 140,
    defaultVisible: false,
  },
  {
    key: 'total_available',
    label: 'Total Available',
    group: 'replenishment',
    format: 'units',
    description: 'Units on hand + on order.',
    align: 'right',
    width: 130,
    defaultVisible: false,
  },
  {
    key: 'last_order_date',
    label: 'Last Order Date',
    group: 'replenishment',
    format: 'date',
    description: 'Most recent PO date.',
    align: 'left',
    width: 130,
    defaultVisible: false,
  },
  {
    key: 'order_cost',
    label: 'Order Cost',
    group: 'replenishment',
    format: 'currency',
    description: 'Suggested reorder units × unit cost.',
    align: 'right',
    width: 120,
    defaultVisible: false,
  },
  {
    key: 'lost_sales',
    label: 'Lost Sales',
    group: 'replenishment',
    format: 'currency',
    description: 'Velocity × out-of-stock days in the lookback × unit price.',
    align: 'right',
    width: 120,
    defaultVisible: false,
    valueBasis: true,
  },
  {
    key: 'net_sales',
    label: 'Net Sales',
    group: 'margin',
    format: 'currency',
    description: 'Net sales in the date range.',
    align: 'right',
    width: 120,
    defaultVisible: false,
  },
  {
    key: 'cash_margin',
    label: 'Cash Margin',
    group: 'margin',
    format: 'currency',
    description: 'Cash margin in the date range.',
    align: 'right',
    width: 120,
    defaultVisible: false,
  },
  {
    key: 'cash_margin_pct',
    label: 'Cash Margin %',
    group: 'margin',
    format: 'percent',
    description: 'Cash margin ÷ net sales.',
    align: 'right',
    width: 130,
    defaultVisible: false,
  },
  {
    key: 'cmroi',
    label: 'CMROI',
    group: 'margin',
    format: 'ratio',
    description: 'Cash margin ÷ average inventory at cost, annualized.',
    align: 'right',
    width: 100,
    defaultVisible: false,
  },
  {
    key: 'initial_margin_pct',
    label: 'Initial Margin %',
    group: 'margin',
    format: 'percent',
    description: '(Receipts at retail − at cost) ÷ receipts at retail.',
    align: 'right',
    width: 130,
    defaultVisible: false,
  },
  {
    key: 'gross_margin_pct',
    label: 'Gross Margin %',
    group: 'margin',
    format: 'percent',
    description: 'Gross margin ÷ net sales.',
    align: 'right',
    width: 130,
    defaultVisible: false,
  },
  {
    key: 'markdown_dollars',
    label: 'Markdown $',
    group: 'margin',
    format: 'currency',
    description: 'Markdown dollars in the date range.',
    align: 'right',
    width: 120,
    defaultVisible: false,
  },
  {
    key: 'sell_through_pct',
    label: 'Sell-Through %',
    group: 'margin',
    format: 'percent',
    description: 'Units sold ÷ units received in the date range.',
    align: 'right',
    width: 130,
    defaultVisible: false,
  },
  {
    key: 'sell_through_start_inv_pct',
    label: 'Sell-Through % (w/ Start Inv)',
    group: 'margin',
    format: 'percent',
    description: 'Units sold ÷ (units sold + on hand).',
    align: 'right',
    width: 190,
    defaultVisible: false,
  },
];

const COLUMN_BY_KEY = new Map<CoreReplenishmentColumnKey, CoreReplenishmentColumnConfig>(CORE_REPLENISHMENT_COLUMNS.map((c) => [c.key, c]));

export const columnByKey = (key: CoreReplenishmentColumnKey): CoreReplenishmentColumnConfig | undefined => COLUMN_BY_KEY.get(key);
export const DEFAULT_COLUMN_ORDER: readonly CoreReplenishmentColumnKey[] = CORE_REPLENISHMENT_COLUMNS.map((c) => c.key);
export const DEFAULT_VISIBLE_COLUMNS: readonly CoreReplenishmentColumnKey[] = CORE_REPLENISHMENT_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key);
export const METRIC_COLUMN_WIDTH = 120;
export const DEFAULT_COLUMN_WIDTHS: Readonly<Record<string, number>> = {
  [PRODUCT_COLUMN_KEY]: PRODUCT_COLUMN_WIDTH,
  ...Object.fromEntries(CORE_REPLENISHMENT_COLUMNS.map((c) => [c.key, c.width])),
};

export const isColumnShown = (
  col: CoreReplenishmentColumnConfig,
  { skuGrain, hasOtb }: { skuGrain: SkuGrain; hasOtb: boolean },
  visible: readonly CoreReplenishmentColumnKey[]
): boolean => visible.includes(col.key) && (!col.skuOnly || skuGrain === 'sku') && (!col.otbOnly || hasOtb);

export const columnLabel = (col: CoreReplenishmentColumnConfig, basis: ValueBasis): string =>
  col.valueBasis ? `${col.label} (${basis === 'cost' ? 'Cost' : 'Retail'})` : col.label;

export const sortTypeOf = (key: CoreReplenishmentSortKey): 'text' | 'numeric' =>
  key === PRODUCT_COLUMN_KEY || columnByKey(key)?.format === 'text' ? 'text' : 'numeric';

// --- Cell values ---------------------------------------------------------------------------------

type LabelledRow = RowFigures & { size_label?: string; color_label?: string };

export function columnValue(row: LabelledRow, key: CoreReplenishmentColumnKey): string | number | null {
  if (key === 'size') return row.size_label && row.size_label !== EMPTY_CELL ? row.size_label : null;
  if (key === 'color') return row.color_label && row.color_label !== EMPTY_CELL ? row.color_label : null;
  if (key === 'status') return row.status;
  const value = row[key];
  return typeof value === 'number' || typeof value === 'string' ? value : null;
}

const decimal = (value: number, digits: number) =>
  value.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

export function formatColumnValue(key: CoreReplenishmentColumnKey, value: string | number | null): string {
  if (value === null || value === undefined || (typeof value === 'number' && !Number.isFinite(value))) return EMPTY_CELL;
  switch (columnByKey(key)?.format) {
    case 'status':
      return statusLabel(value as ReplenishmentStatus);
    case 'units':
      return formatUnits(value as number);
    case 'currency':
      return formatCurrency(value as number);
    case 'date':
      return formatDate(value as string);
    case 'decimal':
    case 'ratio':
      return decimal(value as number, 2);
    case 'weeks':
      return decimal(value as number, 1);
    case 'percent':
      return formatPercent(value as number);
    case 'signed_percent':
      return `${(value as number) > 0 ? '+' : ''}${Math.round(value as number)}%`;
    default:
      return String(value);
  }
}

export const cellText = (row: LabelledRow, key: CoreReplenishmentColumnKey, isGroupRow = false): string => {
  if (!isGroupRow && columnByKey(key)?.groupOnly) return EMPTY_CELL;
  return formatColumnValue(key, columnValue(row, key));
};

const shortDate = (iso: string): string => formatDate(iso).replace(/, \d{4}$/, '');

/** "Oct 28" under On Order; with `markLate`, "Nov 3 · after stockout" when the next PO lands after the stockout. */
export function onOrderSubLabel(row: Pick<RowFigures, 'on_order_units' | 'next_arrival_date' | 'stockout_date'>, markLate = false): string | null {
  if (!(row.on_order_units > 0) || !row.next_arrival_date) return null;
  const arrival = shortDate(row.next_arrival_date);
  return markLate && row.stockout_date && row.next_arrival_date > row.stockout_date ? `${arrival} · after stockout` : arrival;
}

export const reorderSubLabel = (row: Pick<RowFigures, 'target_weeks'>): string | null =>
  row.target_weeks === null ? null : `target ${formatTargetWeeks(Math.round(row.target_weeks * 10) / 10)} wks`;

export function coreLabel(p: Pick<CoreReplenishmentProduct, 'detected_core' | 'row'>): string | null {
  if (p.row.pin_state === 'pinned') return 'Core, pinned';
  if (p.row.pin_state === 'unpinned') return p.detected_core ? 'Unpinned' : null;
  return p.detected_core ? 'Core, detected' : null;
}

/** "4 SKUs · 3 to order · Core, detected". */
export function productSubLine(p: Pick<CoreReplenishmentProduct, 'skus' | 'skus_to_order' | 'detected_core' | 'row'>): string {
  const parts = [`${p.skus.length} ${p.skus.length === 1 ? 'SKU' : 'SKUs'}`, `${p.skus_to_order} to order`];
  const core = coreLabel(p);
  if (core) parts.push(core);
  return parts.join(' · ');
}

const formatTermWeeks = (weeks: number): string => (Number.isInteger(weeks) ? String(weeks) : weeks.toFixed(1));

/** "3 wks lead · 2 wks cadence" under a vendor row. */
export const vendorTermsLine = (terms: VendorTerms): string =>
  `${formatTermWeeks(terms.lead_time_weeks)} wks lead · ${formatTermWeeks(terms.cadence_weeks)} wks cadence`;

export const VENDOR_TERMS_TOOLTIP = 'Lead time and order cadence are client settings, the same for every vendor. Change them in Report settings.';

export const itemCountLabel = (count: number): string => `${count} ${count === 1 ? 'item' : 'items'}`;

export interface FootnoteInput {
  from: string;
  to: string;
  settings: Pick<ReplenishmentSettings, 'velocityLookbackWeeks' | 'coreUplift' | 'includeLeadAndCadence'>;
  wosOverride: number | null;
  // The on-order span's dates; null when the hierarchy shows no OTB.
  otbSpan: string | null;
}

/** The note under the table, worded from the settings in force so it always matches the numbers. */
export function reportFootnote({ from, to, settings, wosOverride, otbSpan }: FootnoteInput): string {
  const lookback = settings.velocityLookbackWeeks;
  const cover = settings.includeLeadAndCadence ? '(lead time + cadence + target weeks)' : 'target weeks';
  const target =
    wosOverride !== null
      ? `target is your weeks of supply target of ${wosOverride}`
      : `target comes from plan: class cover × ${settings.coreUplift} for core items`;
  return [
    `Net Sales, Cash Margin and Sell-Through use the selected date range (${formatDate(from)} – ${formatDate(to)}).`,
    `Velocity is units sold ÷ in-stock days over the last ${lookback} ${lookback === 1 ? 'week' : 'weeks'}.`,
    'Weeks of Supply is units on hand ÷ average weekly units.',
    `Suggested Reorder = velocity × 7 × ${cover} (${target}) − units on hand − units on order that land before the item runs out, never below 0, in units only.`,
    'Group and Total rows add each item’s own suggestion.',
    otbSpan ? `OTB is the class plan’s open-to-buy for ${otbSpan}, worked out by class, so Category and Total rows add up their classes.` : null,
  ]
    .filter(Boolean)
    .join(' ');
}

// --- Sorting -------------------------------------------------------------------------------------

export interface SortConfig {
  key: CoreReplenishmentSortKey;
  direction: 'asc' | 'desc';
}

// Most urgent first, then earliest stockout.
export const DEFAULT_SORT: SortConfig = { key: 'status', direction: 'asc' };

type SortableRow = LabelledRow & {
  row?: {
    product_title?: string;
    variant_title?: string | null;
    sku_id?: string;
  };
};

export function sortValue(row: SortableRow, key: CoreReplenishmentSortKey): string | number | null {
  if (key === PRODUCT_COLUMN_KEY) return row.row?.product_title ?? row.row?.variant_title ?? row.row?.sku_id ?? null;
  if (key === 'status') return row.status === null ? null : statusRank(row.status);
  return columnValue(row, key);
}

const isInsufficient = (row: RowFigures) => row.status === 'insufficient_data' || row.status === null;

/** Insufficient data always sorts last; urgency then stockout date breaks ties. */
export function compareRows(a: SortableRow, b: SortableRow, sort: SortConfig): number {
  const insufficient = Number(isInsufficient(a)) - Number(isInsufficient(b));
  if (insufficient !== 0) return insufficient;
  const primary = compareNullsLast(sortValue(a, sort.key), sortValue(b, sort.key), sort.direction);
  if (primary !== 0) return primary;
  return compareNullsLast(statusRank(a.status), statusRank(b.status), 'asc') || compareNullsLast(a.stockout_date, b.stockout_date, 'asc');
}

export function sortProducts(products: CoreReplenishmentProduct[], sort: SortConfig): CoreReplenishmentProduct[] {
  const stable = <T extends SortableRow>(rows: T[]): T[] =>
    rows
      .map((row, index) => ({ row, index }))
      .sort((a, b) => compareRows(a.row, b.row, sort) || a.index - b.index)
      .map(({ row }) => row);
  return stable(products).map((p) => ({ ...p, skus: stable(p.skus) }));
}

export const sortKeyLabel = (key: CoreReplenishmentSortKey): string => (key === PRODUCT_COLUMN_KEY ? PRODUCT_COLUMN_LABEL : (columnByKey(key)?.label ?? key));

/** The sort as the row-count line shows it, with the column names apart so they can be bolded. */
export function sortDescriptionParts(sort: SortConfig): {
  label: string;
  wording: string;
  thenLabel: string | null;
} {
  if (sort.key === 'status')
    return {
      label: 'Status',
      wording: sort.direction === 'asc' ? 'most urgent first' : 'least urgent first',
      thenLabel: 'Stockout Date',
    };
  const type = sortTypeOf(sort.key);
  const wording = type === 'text' ? (sort.direction === 'asc' ? 'A to Z' : 'Z to A') : sort.direction === 'asc' ? 'low to high' : 'high to low';
  return { label: sortKeyLabel(sort.key), wording, thenLabel: null };
}

export function sortDescription(sort: SortConfig): string {
  const { label, wording, thenLabel } = sortDescriptionParts(sort);
  return `${label} (${wording})${thenLabel ? `, then ${thenLabel}` : ''}`;
}

export const productCountText = (shown: number, total: number, skuCount: number): string =>
  `${shown} of ${total} products shown (${skuCount} sizes/colors total)`;

/** "2 SKUs have insufficient data and sort last", or null when none do. */
export const insufficientSkusText = (count: number): string | null =>
  count > 0 ? `${count} ${count === 1 ? 'SKU has' : 'SKUs have'} insufficient data and sort last` : null;

/** "14 of 16 products shown (35 sizes/colors total) · sorted by Status (most urgent first), then Stockout Date · 2 SKUs have insufficient data and sort last". */
export function rowCountLine(shown: number, total: number, skuCount: number, sort: SortConfig, insufficientSkus = 0): string {
  return [productCountText(shown, total, skuCount), `sorted by ${sortDescription(sort)}`, insufficientSkusText(insufficientSkus)].filter(Boolean).join(' · ');
}

export const countInsufficientSkus = (products: CoreReplenishmentProduct[]): number =>
  products.reduce((count, p) => count + p.skus.filter((s) => s.status === 'insufficient_data').length, 0);

// --- Hierarchy, groups and totals ----------------------------------------------------------------

export interface CoreHierarchyOption {
  key: CoreReplenishmentHierarchy;
  label: string;
  hasOtb: boolean;
}

export const WITH_OTB_HIERARCHY_OPTIONS: readonly CoreHierarchyOption[] = [
  ...SHARED_WITH_OTB_HIERARCHY_OPTIONS,
  // Vendor rows sit under their class, so the class rows still carry the plan's OTB.
  { key: 'class_vendor', label: 'Class › Vendor › Product', hasOtb: true },
];
export const WITHOUT_OTB_HIERARCHY_OPTIONS: readonly CoreHierarchyOption[] = SHARED_WITHOUT_OTB_HIERARCHY_OPTIONS;
export const HIERARCHY_OPTIONS: readonly CoreHierarchyOption[] = [...WITH_OTB_HIERARCHY_OPTIONS, ...WITHOUT_OTB_HIERARCHY_OPTIONS];
export const DEFAULT_HIERARCHY: CoreReplenishmentHierarchy = 'class_vendor';

export const hierarchyHasOtb = (hierarchy: CoreReplenishmentHierarchy): boolean => HIERARCHY_OPTIONS.find((opt) => opt.key === hierarchy)?.hasOtb ?? false;

export const hierarchyGroupNoun = (hierarchy: CoreReplenishmentHierarchy): string | null =>
  ({
    total: null,
    category: 'Category',
    class: 'Class',
    class_vendor: 'Class',
    vendor: 'Vendor',
    location: 'Location',
  })[hierarchy];

// Class › Vendor › Product groups by class first; the vendor rows nest inside.
const isClassGrouping = (hierarchy: CoreReplenishmentHierarchy): boolean => hierarchy === 'class' || hierarchy === 'class_vendor';

export const NO_GROUP_VALUE = '(none)';

/** Sum of class plans; Total takes every plan, Category its classes, Class its own. Null without a plan. */
export function sumClassOtb(plans: ClassPlanRow[], basis: ValueBasis): OtbFigures | null {
  const figures = plans.map((p) => p[basis]).filter(Boolean);
  if (figures.length === 0) return null;
  const add = (key: keyof OtbFigures) => sumNullable(figures.map((f) => f[key]));
  return {
    otb: add('otb'),
    on_order: add('on_order'),
    plan_sales: add('plan_sales'),
    planned_inflow: add('planned_inflow'),
    plan_next_month_inv: add('plan_next_month_inv'),
  };
}

/** Real subtotals over products, with OTB from the plan when the hierarchy has it. */
export function aggregateFigures(products: CoreReplenishmentProduct[], otb: OtbFigures | null, periodWeeks: number): RowFigures {
  const total = (key: keyof RowFigures) => sum(products.map((p) => (p[key] as number) || 0));
  const nullableTotal = (key: keyof RowFigures) => sumNullable(products.map((p) => p[key] as number | null));
  const unitsOh = total('units_oh');
  const velocity = nullableTotal('velocity_per_day');
  const avgWeekly = velocity === null ? null : velocity * DAYS_PER_WEEK;
  const suggestedDollars = nullableTotal('suggested_dollars');
  const sales = sumSales(products.flatMap((p) => p.skus.map((s) => s.row.sales ?? EMPTY_SALES)));
  return {
    status: mostUrgentStatus(products.map((p) => p.status)),
    insufficient_reason: null,
    units_oh: unitsOh,
    on_order_units: total('on_order_units'),
    total_available: total('total_available'),
    next_arrival_date: minIso(products.map((p) => p.next_arrival_date)),
    last_order_date: maxIso(products.map((p) => p.last_order_date)),
    velocity_per_day: velocity,
    in_stock_days: null,
    avg_weekly_units: avgWeekly,
    trend_pct: null,
    trend_flagged: false,
    days_to_stockout: null,
    stockout_date: minIso(products.map((p) => p.stockout_date)),
    weeks_of_supply: avgWeekly ? unitsOh / avgWeekly : null,
    target_weeks: null,
    suggested_units: nullableTotal('suggested_units'),
    suggested_dollars: suggestedDollars,
    order_cost: nullableTotal('order_cost'),
    lost_sales: nullableTotal('lost_sales'),
    ...salesRatios(sales, unitsOh, periodWeeks),
    otb: otb?.otb ?? null,
    otb_after: otb?.otb === null || otb?.otb === undefined ? null : otb.otb - (suggestedDollars ?? 0),
    otb_on_order: otb?.on_order ?? null,
    plan_sales: otb?.plan_sales ?? null,
    planned_inflow: otb?.planned_inflow ?? null,
    plan_next_month_inv: otb?.plan_next_month_inv ?? null,
  };
}

const groupKeyOf = (p: CoreReplenishmentProduct, hierarchy: CoreReplenishmentHierarchy): string => {
  if (hierarchy === 'category') return p.row.category || NO_GROUP_VALUE;
  if (isClassGrouping(hierarchy)) return p.row.class_code || NO_GROUP_VALUE;
  if (hierarchy === 'vendor') return p.row.vendor || NO_GROUP_VALUE;
  if (hierarchy === 'location') return p.row.location_code || NO_GROUP_VALUE;
  return '';
};

// Plan and catalog spell categories independently, so they match ignoring case and padding.
const categoryKey = (category: string | null | undefined): string => (category ?? '').trim().toUpperCase() || NO_GROUP_VALUE;

const plansFor = (hierarchy: CoreReplenishmentHierarchy, key: string | null, ctx: ComputeContext): ClassPlanRow[] => {
  const plans = Array.from(ctx.classPlans.values());
  if (hierarchy === 'total') return plans;
  if (hierarchy === 'category') return plans.filter((p) => categoryKey(p.category) === categoryKey(key));
  if (isClassGrouping(hierarchy)) return plans.filter((p) => p.class_code === key);
  return [];
};

// Keeps first-seen order, so groups follow their top-ranked product.
function bucketProducts(products: CoreReplenishmentProduct[], keyOf: (p: CoreReplenishmentProduct) => string): Map<string, CoreReplenishmentProduct[]> {
  const byKey = new Map<string, CoreReplenishmentProduct[]>();
  products.forEach((p) => {
    const key = keyOf(p);
    const list = byKey.get(key);
    if (list) list.push(p);
    else byKey.set(key, [p]);
  });
  return byKey;
}

const GROUP_KEY_SEPARATOR = '\u001f';

/** Vendor rows (no OTB: plans have no vendor). Under a class, keys carry the class so collapse state stays per class. */
function vendorGroups(products: CoreReplenishmentProduct[], parentKey: string | null, ctx: ComputeContext): CoreReplenishmentGroup[] {
  return Array.from(bucketProducts(products, (p) => p.row.vendor || NO_GROUP_VALUE).entries()).map(([vendor, list]) => ({
    key: parentKey === null ? vendor : `${parentKey}${GROUP_KEY_SEPARATOR}${vendor}`,
    label: vendor,
    code: null,
    products: list,
    statusCounts: countStatuses(list),
    figures: aggregateFigures(list, null, ctx.periodWeeks),
    terms: resolveVendorTerms(ctx.settings),
    vendor,
    children: [],
  }));
}

/** Groups in order of their top-ranked product, so sort products first. Empty for Total by Product. */
export function groupProducts(products: CoreReplenishmentProduct[], hierarchy: CoreReplenishmentHierarchy, ctx: ComputeContext): CoreReplenishmentGroup[] {
  if (hierarchy === 'total') return [];
  if (hierarchy === 'vendor') return vendorGroups(products, null, ctx);
  return Array.from(bucketProducts(products, (p) => groupKeyOf(p, hierarchy)).entries()).map(([key, list]) => {
    const first = list[0].row;
    const isClass = isClassGrouping(hierarchy);
    const label = isClass ? first.class_name || key : hierarchy === 'location' ? first.location_name || key : key;
    const otb = hierarchyHasOtb(hierarchy) ? sumClassOtb(plansFor(hierarchy, key, ctx), ctx.valueBasis) : null;
    return {
      key,
      label,
      code: (isClass || hierarchy === 'location') && key !== NO_GROUP_VALUE ? key : null,
      products: list,
      statusCounts: countStatuses(list),
      figures: aggregateFigures(list, otb, ctx.periodWeeks),
      terms: null,
      vendor: null,
      children: hierarchy === 'class_vendor' ? vendorGroups(list, key, ctx) : [],
    };
  });
}

export function computeTotalRow(products: CoreReplenishmentProduct[], hierarchy: CoreReplenishmentHierarchy, ctx: ComputeContext): RowFigures {
  const otb = hierarchyHasOtb(hierarchy) ? sumClassOtb(plansFor('total', null, ctx), ctx.valueBasis) : null;
  return aggregateFigures(products, otb, ctx.periodWeeks);
}

// Export order icon on a vendor row only when it has units to order.
export const groupHasUnitsToOrder = (group: Pick<CoreReplenishmentGroup, 'figures'>): boolean => (group.figures.suggested_units ?? 0) > 0;

// --- CSV exports ---------------------------------------------------------------------------------

const TEXT_FORMATS: readonly ColumnFormat[] = ['text', 'status'];

// Raw numbers and ISO dates so a spreadsheet can total and sort them.
function csvValue(row: LabelledRow, key: CoreReplenishmentColumnKey): string {
  const value = columnValue(row, key);
  if (value === null) return '';
  if (columnByKey(key)?.format === 'status') return statusLabel(value as ReplenishmentStatus);
  if (typeof value === 'number') return String(Math.round(value * 100) / 100);
  return value;
}

export interface CoreReplenishmentCsvOptions {
  columns: readonly CoreReplenishmentColumnConfig[];
  basis: ValueBasis;
  includeSkus: boolean;
  hideZeroSalesSkus: boolean;
  // Group column ("Category"), or null for Total by Product.
  groupHeader: string | null;
  groupOf?: (p: CoreReplenishmentProduct) => string;
}

/** Leaf rows (products, or SKUs at SKU grain) in on-screen order, visible columns only. */
export function buildCoreReplenishmentCsv(products: CoreReplenishmentProduct[], options: CoreReplenishmentCsvOptions): string {
  const { columns, basis, includeSkus, hideZeroSalesSkus, groupHeader, groupOf } = options;
  const leafColumns = columns.filter((c) => !c.groupOnly);
  const lead = groupHeader ? [groupHeader] : [];
  const header = [...lead, PRODUCT_COLUMN_LABEL, 'SKU', 'Vendor', ...leafColumns.map((c) => columnLabel(c, basis))].map((h) => csvCell(h, true));
  const lines = [header.join(',')];
  const cells = (row: LabelledRow) => leafColumns.map((c) => csvCell(csvValue(row, c.key), TEXT_FORMATS.includes(c.format)));
  products.forEach((p) => {
    const prefix = [...(groupHeader && groupOf ? [csvCell(groupOf(p), true)] : []), csvCell(p.row.product_title, true)];
    const vendor = csvCell(p.row.vendor ?? '', true);
    if (!includeSkus) {
      lines.push([...prefix, '', vendor, ...cells(p)].join(','));
      return;
    }
    visibleSkus(p, hideZeroSalesSkus).forEach((s) => lines.push([...prefix, csvCell(s.row.sku_id, true), vendor, ...cells(s)].join(',')));
  });
  return lines.join('\n');
}

export const groupValueOf = (p: CoreReplenishmentProduct, hierarchy: CoreReplenishmentHierarchy): string => {
  if (isClassGrouping(hierarchy)) return p.row.class_code ? `${p.row.class_code} ${p.row.class_name ?? ''}`.trim() : NO_GROUP_VALUE;
  if (hierarchy === 'location') return p.row.location_code ? `${p.row.location_code} ${p.row.location_name ?? ''}`.trim() : NO_GROUP_VALUE;
  return groupKeyOf(p, hierarchy);
};

export interface OrderLine {
  vendor: string;
  sku_id: string;
  barcode: string;
  product_title: string;
  size: string;
  color: string;
  quantity: number;
  unit_cost: number | null;
  total_cost: number | null;
  expected_arrival: string | null;
  class_label: string;
}

/** One line per SKU that needs units, grouped by vendor; pass `vendor` for a vendor row's own export. */
export function buildOrderLines(products: CoreReplenishmentProduct[], vendor?: string): OrderLine[] {
  const lines = products
    .filter((p) => vendor === undefined || (p.row.vendor || NO_GROUP_VALUE) === vendor)
    .flatMap((p) =>
      p.skus
        .filter((s) => (s.suggested_units ?? 0) > 0)
        .map((s) => ({
          vendor: p.row.vendor || NO_GROUP_VALUE,
          sku_id: s.row.sku_id,
          barcode: s.row.barcode ?? '',
          product_title: p.row.product_title,
          size: s.size_label === EMPTY_CELL ? '' : s.size_label,
          color: s.color_label === EMPTY_CELL ? '' : s.color_label,
          quantity: s.suggested_units as number,
          unit_cost: s.row.unit_cost,
          total_cost: s.order_cost,
          expected_arrival: s.expected_arrival,
          class_label: groupValueOf(p, 'class'),
        }))
    );
  // Stable by vendor so each vendor's lines stay in on-screen order.
  return lines
    .map((line, i) => ({ line, i }))
    .sort((a, b) => a.line.vendor.localeCompare(b.line.vendor) || a.i - b.i)
    .map(({ line }) => line);
}

const money = (v: number | null) => (v === null ? '' : String(Math.round(v * 100) / 100));

/** Order lines, then a class summary of SKUs, units and cost. */
export function buildOrdersCsv(lines: OrderLine[]): string {
  const header = ['Vendor', 'SKU', 'Barcode', 'Product', 'Size', 'Color', 'Quantity', 'Unit Cost', 'Total Cost', 'Expected Arrival'];
  const out = [header.map((h) => csvCell(h, true)).join(',')];
  lines.forEach((l) =>
    out.push(
      [
        csvCell(l.vendor, true),
        csvCell(l.sku_id, true),
        csvCell(l.barcode, true),
        csvCell(l.product_title, true),
        csvCell(l.size, true),
        csvCell(l.color, true),
        String(l.quantity),
        money(l.unit_cost),
        money(l.total_cost),
        l.expected_arrival ?? '',
      ].join(',')
    )
  );
  const byClass = new Map<string, { skus: number; quantity: number; cost: number }>();
  lines.forEach((l) => {
    const entry = byClass.get(l.class_label) ?? {
      skus: 0,
      quantity: 0,
      cost: 0,
    };
    byClass.set(l.class_label, {
      skus: entry.skus + 1,
      quantity: entry.quantity + l.quantity,
      cost: entry.cost + (l.total_cost ?? 0),
    });
  });
  const classes = Array.from(byClass.entries()).sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }));
  out.push('', csvCell('Class Summary', true), ['Class', 'SKUs', 'Quantity', 'Total Cost'].map((h) => csvCell(h, true)).join(','));
  classes.forEach(([label, c]) => out.push([csvCell(label, true), String(c.skus), String(c.quantity), money(c.cost)].join(',')));
  const total = classes.reduce(
    (acc, [, c]) => ({
      skus: acc.skus + c.skus,
      quantity: acc.quantity + c.quantity,
      cost: acc.cost + c.cost,
    }),
    { skus: 0, quantity: 0, cost: 0 }
  );
  out.push([csvCell('Total', true), String(total.skus), String(total.quantity), money(total.cost)].join(','));
  return out.join('\n');
}

// --- Filters, controls and saved views -----------------------------------------------------------

export const CUSTOM_DATE_RANGE = 'Custom range...';
export const DATE_RANGE_PRESETS = ['1 Month', '3 Months', '6 Months', CUSTOM_DATE_RANGE] as const;
export const DEFAULT_DATE_RANGE = '3 Months';
export const DEFAULT_ON_ORDER_SPAN: OnOrderSpanKey = '3_months';
export const PAGE_SIZE = 25;

export const DEFAULT_FILTERS: CoreReplenishmentFilterState = {
  dateRange: DEFAULT_DATE_RANGE,
  customStartDate: '',
  customEndDate: '',
  locations: [],
  excludeLocations: false,
  categories: [],
  excludeCategories: false,
  classes: [],
  excludeClasses: false,
  vendors: [],
  excludeVendors: false,
  statuses: [],
  coreItems: 'detected_pinned',
};

export const DEFAULT_CONTROLS: CoreReplenishmentControls = {
  onOrderSpan: DEFAULT_ON_ORDER_SPAN,
  spanStart: '',
  spanEnd: '',
  valueBasis: 'retail',
  skuGrain: 'product',
  wosOverride: null,
  hideCovered: false,
  hideZeroSalesSkus: false,
};

export interface CoreReplenishmentViewPayload {
  filters: CoreReplenishmentFilterState;
  controls: CoreReplenishmentControls;
  hierarchy: CoreReplenishmentHierarchy;
  // View-level overrides only; client settings stay live.
  settings: Partial<ReplenishmentSettings>;
  sort: SortConfig;
  visibleColumns: CoreReplenishmentColumnKey[];
  columnOrder: CoreReplenishmentColumnKey[];
  pinnedColumns: CoreReplenishmentColumnKey[];
}

/** Drops client-level keys, which are stored per client rather than per view. */
export const viewSettings = (overrides: Partial<ReplenishmentSettings>): Partial<ReplenishmentSettings> =>
  Object.fromEntries(Object.entries(sanitizePartialSettings(overrides)).filter(([key]) => !CLIENT_SETTING_KEYS.includes(key as NumericSettingKey)));

const asRecord = (value: unknown): Record<string, unknown> => (value && typeof value === 'object' ? (value as Record<string, unknown>) : {});
const asString = (value: unknown): string => (typeof value === 'string' ? value : '');
const asStringArray = (value: unknown): string[] => (Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []);
const knownColumns = (value: unknown): CoreReplenishmentColumnKey[] =>
  asStringArray(value).filter((k): k is CoreReplenishmentColumnKey => COLUMN_BY_KEY.has(k as CoreReplenishmentColumnKey));
const oneOf = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T => (allowed.includes(value as T) ? (value as T) : fallback);

const SORT_KEYS: readonly CoreReplenishmentSortKey[] = [PRODUCT_COLUMN_KEY, ...DEFAULT_COLUMN_ORDER];
const STATUS_KEYS = STATUS_OPTIONS.map((s) => s.key);
const SPAN_KEYS: readonly OnOrderSpanKey[] = [...ON_ORDER_SPAN_OPTIONS, 'custom'];

/** A saved view made safe to apply: unknown values drop and missing ones fall back to defaults. */
export function normalizeViewPayload(raw: unknown): CoreReplenishmentViewPayload {
  const payload = asRecord(raw);
  const f = asRecord(payload.filters);
  const c = asRecord(payload.controls);
  const sort = asRecord(payload.sort);
  return {
    filters: {
      dateRange: oneOf(f.dateRange, DATE_RANGE_PRESETS, DEFAULT_DATE_RANGE),
      customStartDate: asString(f.customStartDate),
      customEndDate: asString(f.customEndDate),
      locations: asStringArray(f.locations),
      excludeLocations: f.excludeLocations === true,
      categories: asStringArray(f.categories),
      excludeCategories: f.excludeCategories === true,
      classes: asStringArray(f.classes),
      excludeClasses: f.excludeClasses === true,
      vendors: asStringArray(f.vendors),
      excludeVendors: f.excludeVendors === true,
      statuses: asStringArray(f.statuses).filter((s): s is ReplenishmentStatus => STATUS_KEYS.includes(s as ReplenishmentStatus)),
      coreItems: oneOf(
        f.coreItems,
        CORE_ITEMS_OPTIONS.map((o) => o.key),
        DEFAULT_FILTERS.coreItems
      ),
    },
    controls: {
      onOrderSpan: oneOf(c.onOrderSpan, SPAN_KEYS, DEFAULT_ON_ORDER_SPAN),
      spanStart: asString(c.spanStart),
      spanEnd: asString(c.spanEnd),
      valueBasis: c.valueBasis === 'cost' ? 'cost' : 'retail',
      skuGrain: c.skuGrain === 'sku' ? 'sku' : 'product',
      wosOverride: parseWosOverride(c.wosOverride as number | null),
      hideCovered: c.hideCovered === true,
      hideZeroSalesSkus: c.hideZeroSalesSkus === true,
    },
    hierarchy: oneOf(
      payload.hierarchy,
      HIERARCHY_OPTIONS.map((o) => o.key),
      DEFAULT_HIERARCHY
    ),
    settings: viewSettings(asRecord(payload.settings) as Partial<ReplenishmentSettings>),
    sort: {
      key: oneOf(sort.key, SORT_KEYS, DEFAULT_SORT.key),
      direction: sort.direction === 'desc' ? 'desc' : sort.direction === 'asc' ? 'asc' : DEFAULT_SORT.direction,
    },
    visibleColumns: Array.isArray(payload.visibleColumns) ? knownColumns(payload.visibleColumns) : [...DEFAULT_VISIBLE_COLUMNS],
    columnOrder: knownColumns(payload.columnOrder),
    pinnedColumns: knownColumns(payload.pinnedColumns),
  };
}

type StorageUserId = string | number | null | undefined;
const storageScope = (userId: StorageUserId, clientId: string | number) => `${userId ?? 'anonymous'}-${clientId}`;

export const CORE_REPLENISHMENT_STORAGE_PREFIX = 'core-replenishment';
export const getColumnOrderStorageKey = (userId: StorageUserId, clientId: string | number) =>
  `${CORE_REPLENISHMENT_STORAGE_PREFIX}-column-order-${storageScope(userId, clientId)}`;
export const getColumnWidthsStorageKey = (userId: StorageUserId, clientId: string | number) =>
  `${CORE_REPLENISHMENT_STORAGE_PREFIX}-column-widths-${storageScope(userId, clientId)}`;
export const getPinnedColumnsStorageKey = (userId: StorageUserId, clientId: string | number) =>
  `${CORE_REPLENISHMENT_STORAGE_PREFIX}-pinned-columns-${storageScope(userId, clientId)}`;
